import { useEffect } from "preact/hooks";
import PublicHeader from "../components/PublicHeader.js";
import PublicFooter from "../components/PublicFooter.js";

const publishedAt = "2026-03-11";
const articleUrl = "https://webhookstorage.com/blog/webhookstorage-is-live";

const sections = [
  {
    title: "What it does",
    paragraphs: [
      "webhookstorage sits between the system sending your webhooks and the platform consuming them. When a webhook arrives, we store the full payload in S3 and send your automation tool a lightweight notification with a signed download link. Your workflow fetches the payload when it actually needs it — no size limits, no silent drops.",
      "That is the entire product. Receive, store, retrieve. One static IP you can allowlist. Per-endpoint secrets for authentication. Signed S3 links that expire in 15 minutes. An event trail so you can re-fetch anything that failed downstream.",
    ],
  },
  {
    title: "Why this exists",
    paragraphs: [
      "I kept running into the same problem. Zapier, Make, and n8n all have request-body size limits — usually somewhere between 5 and 16 MB. When a Shopify order payload, a product catalog export, or an image-heavy webhook exceeds that limit, the automation platform silently drops it or returns a vague error. The webhook sender thinks it succeeded. The consumer never got the data. Nobody finds out until something downstream breaks.",
      "The workarounds are all ugly: strip fields from the payload, split it into multiple requests, poll an API later for the full body. Each one adds complexity and fragility. The clean fix is to separate transport from processing — accept the full payload once, store it durably, and hand the consumer a small reference it can fetch on demand.",
      "That is what webhookstorage does. I built it because I needed it, and because the alternatives were either expensive enterprise middleware or duct-tape scripts that break on the next schema change.",
    ],
  },
  {
    title: "What it costs",
    paragraphs: [
      "There are two tiers for self-serve and a business option if you need more.",
      "Free: 5 webhooks per month, 1-day payload retention, no credit card required, yours forever. Same endpoints, same signed links, same static IP as paying customers. Enough to prove the integration works end-to-end before you commit anything.",
      "Activated: $25 one-time activation, which includes $25 in credits. After the credits are consumed, it is $0.03 per webhook and $0.10 per GB-month of storage. 7-day payload retention. No subscriptions, no seat licenses, no overage surprises.",
      "Business: Custom limits, retention policies, and support. If your team needs something the self-serve plans do not cover, book a call and we will figure it out together.",
    ],
  },
  {
    title: "How it works, technically",
    paragraphs: [
      "The ingest endpoint runs on AWS behind a Network Load Balancer with a fixed Elastic IP. Payloads are written to S3 with KMS encryption and per-tenant key isolation. Event metadata — timestamp, size, content type, sender IP — goes into DynamoDB. The outbound notification to your automation platform is a small JSON object: event ID, endpoint ID, payload size, and a presigned S3 download URL.",
      "Your automation tool receives that notification, makes an HTTP GET to the signed URL, and gets the full payload. No API key needed for the download itself — the signature in the URL handles authorization. The link expires in 15 minutes, but the payload stays in storage for your retention period. You can retrieve it again through the API or dashboard at any time before it expires.",
      "Authentication is per-endpoint. Each endpoint gets its own endpoint secret and outbound URL. You can run one endpoint or a hundred — they are isolated from each other. If you need to rotate a secret, the old one keeps working until you explicitly revoke it.",
    ],
  },
  {
    title: "What is already shipped",
    paragraphs: [
      "This is not a landing page for a waitlist. The product is live and accepting traffic. Here is what you can use today: endpoint management with per-endpoint secrets and outbound URLs, webhook ingest up to 250 MB per payload, S3 storage with signed retrieval links, a full event trail, API key management, integration guides for Shopify, Zapier, Make, and n8n, and a usage dashboard with daily charts.",
      "The codebase is TypeScript end to end. The frontend is Preact with zero runtime dependencies — no npm packages in the browser bundle. The backend is SST v3 on AWS with Hono, Lambda, DynamoDB, and S3. Auth is WorkOS AuthKit. Billing is Stripe metered.",
    ],
  },
  {
    title: "Who this is for",
    paragraphs: [
      "If you are an automation builder whose Zapier or Make scenarios break on large payloads, this solves it. If you are an ops team that needs a fixed IP for webhook traffic and per-endpoint auth, this provides both. If you run an agency with multiple clients and need isolated webhook routing, each client gets their own endpoint.",
      "If you are a developer who just wants to capture and inspect webhook payloads during development, the free tier does that without any payment setup.",
    ],
  },
  {
    title: "What is next",
    paragraphs: [
      "The core product is stable and I am using it in production. Near-term priorities are additional platform guides, webhook signature verification for more source providers, and retention policy controls per endpoint. Longer-term, I am looking at event replay, payload transformation rules, and a CLI for local development.",
      "If you have a use case that does not fit the current product, I want to hear about it. The fastest way to reach me is through the business booking link or the support email in the dashboard.",
    ],
  },
];

export default function BlogLaunch() {
  useEffect(() => {
    document.title = "webhookstorage is live — Webhook buffer for large payloads";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "webhookstorage is a webhook buffer service that captures large payloads, stores them in S3, and hands your automation platform a signed download link. Free tier included, pay-as-you-go after $25 activation.",
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", articleUrl);

    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: "webhookstorage is live",
      description:
        "Announcing webhookstorage — a webhook buffer service for large payloads. Free tier, $25 activation for pay-as-you-go, and business plans available.",
      datePublished: publishedAt,
      dateModified: publishedAt,
      author: {
        "@type": "Organization",
        name: "webhookstorage",
      },
      publisher: {
        "@type": "Organization",
        name: "webhookstorage",
        url: "https://webhookstorage.com",
      },
      mainEntityOfPage: articleUrl,
      url: articleUrl,
      keywords: [
        "webhook buffer",
        "large webhook payload",
        "webhook storage service",
        "Zapier payload limit",
        "Make webhook size limit",
      ],
    });
    document.head.appendChild(jsonLd);

    return () => {
      jsonLd.remove();
      if (canonical?.getAttribute("href") === articleUrl) {
        canonical.remove();
      }
    };
  }, []);

  return (
    <div class="min-h-screen bg-slate-50 text-slate-900">
      <PublicHeader current="blog" />

      <main class="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <p class="text-sm font-semibold uppercase tracking-[0.24em] text-teal-700">
          Launch
        </p>
        <h1 class="mt-4 text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
          webhookstorage is live
        </h1>
        <div class="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
          <span>{publishedAt}</span>
          <span>•</span>
          <span>Ben Sammons</span>
        </div>

        <p class="mt-8 text-lg leading-8 text-slate-600">
          webhookstorage is a small, focused service: it receives webhook
          payloads that are too large for your automation platform, stores them
          in S3, and gives your workflow a signed link to fetch the full body on
          demand. It is live, it is accepting traffic, and the free tier does
          not require a credit card.
        </p>

        <article class="mt-10 space-y-12">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 class="text-2xl font-semibold tracking-tight text-slate-950">
                {section.title}
              </h2>
              <div class="mt-4 space-y-5 text-base leading-8 text-slate-700">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </article>

        <section class="mt-14 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <h2 class="text-2xl font-semibold tracking-tight text-slate-950">
            Get started
          </h2>
          <div class="mt-4 space-y-3 text-base leading-7 text-slate-700">
            <p>
              <strong>Free:</strong> 5 webhooks/month, 1-day retention, no
              credit card. Sign up and start sending test payloads in minutes.
            </p>
            <p>
              <strong>Activated:</strong> $25 one-time activation (includes
              $25 in credits). $0.03/webhook + $0.10/GB-month after credits.
              7-day retention.
            </p>
            <p>
              <strong>Business:</strong> Custom limits, retention, and support.
            </p>
          </div>
          <div class="mt-6 flex flex-wrap gap-3">
            <a
              href="/login"
              class="rounded-full bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-800"
            >
              Create a free account
            </a>
            <a
              href="https://calendar.app.google/XdnNT3AGfjGqFRi49"
              class="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:border-slate-400 hover:bg-slate-50"
            >
              Book a call (Business)
            </a>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
